import { VOICE_NOTES, type VoiceNote } from "./voice-notes";
import { VOICE_POSTS, type VoicePost } from "./voice-posts";

/**
 * 受講生の投稿の壁 (HP の受講生の声と、適職診断の結果の頁で同じものを出す。データと部品と CSS はここが正本)。
 * 見出しと説明の文は置き場ごとに違うので、ここは壁の本体だけ。見出し側は voiceWallSources で出どころの名前を取る。
 * 色と余白は --vw-* の変数で受ける (VOICE_WALL_CSS の頭に一覧)。
 * 受講生の投稿の壁。受講生が X と note に自分で書いた投稿を、白い札にして 2 段で横に流す。上の段は左へ、下の段は右へ。
 * 札は、アイコン、表示名、@ハンドル (note は note.com/<書き手>)、右上に X か note の印、本文の原文。札ごと元の投稿へのリンク (別タブ)。
 *
 * 載せるのは受講生が自分で公開している投稿だけ (2026-10-21 に代表が、本人に 1 件ずつ聞かずに載せてよいと決めた)。足し方は voice-posts.ts (X) と voice-notes.ts (note) の頭に書いた。
 * 両方とも空のあいだは何も描かない。
 *
 * 動きは CSS だけ (voice-wall-css.ts の VOICE_WALL_CSS)。書き出し (apps/women-astro) は React を動かさないので、JS に頼らない。
 * 列を 2 回並べて -50% まで動かすので、1 周して先頭に戻っても切れ目が見えない。2 回目は aria-hidden で、リンクにも Tab で入らない。
 * 札が少ないと 1 回分が画面の幅に足りず、流れの途中で空きが出る。そこで 1 回分が MIN_PER_ROW 枚になるまで同じ並びを繰り返す (繰り返しの分も aria-hidden)。
 * 乗せても止めない (代表の指示。流れる列は hover で止めない)。キーボードのフォーカスがある間だけ止まる。動きを減らす設定では止めて、1 回分だけを指で横に送れる列にする。
 */

type WallItem = { kind: "x" | "note"; key: string; url: string; name: string; sub: string; text: string; date: string; avatar?: string; title?: string };

/** 1 回分の札の最少の数。PC の幅 (札 300px と間 16px で約 1440px) を埋める数 */
const MIN_PER_ROW = 6;
/** 札 1 枚が流れきる秒数。段の長さに比例させ、どの段も同じ速さで読めるようにする */
const SEC_PER_CARD = 9;

const fromPost = (p: VoicePost): WallItem => ({
  kind: "x",
  key: p.url,
  url: p.url,
  name: p.name,
  sub: `@${p.handle}`,
  text: p.text,
  date: p.date,
  avatar: p.avatar,
});

/** note の書き手の住所 (note.com/<書き手>)。X の @ハンドルと同じ位置に出す。記事の URL から切り出す */
const noteCreator = (url: string) => url.replace(/^https:\/\/note\.com\/([^/]+)\/.*$/, "note.com/$1");

const fromNote = (n: VoiceNote): WallItem => ({
  kind: "note",
  key: n.url,
  url: n.url,
  name: n.author,
  sub: noteCreator(n.url),
  // note の本文の太字の印 (**) は、そのまま出ると記号に見えるので外す (字は残す)
  text: n.excerpt.replace(/\*\*/g, ""),
  date: n.date,
  avatar: n.avatar,
  title: n.title,
});

/**
 * X と note を交互に混ぜ (片方が尽きたら残りを続け)、上と下の段へ 0,1,1,0 の順で配る。
 * 単に交互に配ると、上の段が X だけ、下の段が note だけになるため。
 * 出どころが片方だけのときは混ぜる相手がいないので、1 件おきに配る (偶数番目が上、奇数番目が下)。
 * 並べ順がそのまま段の中の順になり、データの側で同じ書き手が隣り合わないようにしてある
 */
function splitRows(posts: VoicePost[], notes: VoiceNote[]): WallItem[][] {
  const mixed: WallItem[] = [];
  for (let i = 0; i < Math.max(posts.length, notes.length); i++) {
    if (posts[i]) mixed.push(fromPost(posts[i]));
    if (notes[i]) mixed.push(fromNote(notes[i]));
  }
  const rows: WallItem[][] = [[], []];
  const single = posts.length === 0 || notes.length === 0;
  mixed.forEach((item, i) => rows[single ? i % 2 : i % 4 === 0 || i % 4 === 3 ? 0 : 1].push(item));
  return rows.filter((r) => r.length > 0);
}

const LOGO = {
  x: { src: "/assets/x_logo.svg", w: 14, h: 14, label: "X の投稿" },
  note: { src: "/assets/note_logo.svg", w: 40, h: 15, label: "note の記事" },
} as const;

function WallCard({ item, hidden, onCardClick }: { item: WallItem; hidden: boolean; onCardClick?: (c: { kind: "x" | "note"; url: string }) => void }) {
  const logo = LOGO[item.kind];
  return (
    <li aria-hidden={hidden || undefined} data-wall-copy={hidden ? "" : undefined}>
      <a className="vw-c" href={item.url} target="_blank" rel="noopener noreferrer" tabIndex={hidden ? -1 : undefined} onClick={() => onCardClick?.({ kind: item.kind, url: item.url })}>
        <span className="vw-c-hd">
          {item.avatar ? (
            <img className="vw-c-av" src={item.avatar} alt="" width={40} height={40} loading="lazy" decoding="async" />
          ) : (
            <span className="vw-c-av vw-c-av-none" aria-hidden="true">
              {Array.from(item.name)[0]}
            </span>
          )}
          <b className="vw-c-name">{item.name}</b>
          <img className={`vw-c-logo vw-c-logo-${item.kind}`} src={logo.src} alt="" width={logo.w} height={logo.h} />
          <span className="vw-c-sub">{item.sub}</span>
        </span>
        <span className="vw-c-tx">{item.text}</span>
        <time className="vw-c-date" dateTime={item.date}>
          {item.date.replace(/-/g, ".")}
        </time>
        <span className="sr-only">（{logo.label}{item.title ? `「${item.title}」` : ""}。新しいタブで開きます）</span>
      </a>
    </li>
  );
}

/** 出どころの名前。X の投稿が無いあいだは note だけと名乗り、X が足されれば自動で「X・note」に戻る。壁が空なら null */
export function voiceWallSources(posts: VoicePost[] = VOICE_POSTS, notes: VoiceNote[] = VOICE_NOTES): "X・note" | "X" | "note" | null {
  if (posts.length + notes.length === 0) return null;
  return posts.length > 0 && notes.length > 0 ? "X・note" : posts.length > 0 ? "X" : "note";
}

/** note のハッシュタグの頁 (受講生が #ゆめさくエンジニア で書いた記事の一覧) */
export const VOICE_WALL_HASHTAG_URL = "https://note.com/hashtag/%E3%82%86%E3%82%81%E3%81%95%E3%81%8F%E3%82%A8%E3%83%B3%E3%82%B8%E3%83%8B%E3%82%A2";

/** 壁の本体 (2 段の流れる列)。onCardClick は札を押したとき (計測用)。載せる投稿が無ければ何も描かない */
function WallRow({ items, reverse, onCardClick }: { items: WallItem[]; reverse: boolean; onCardClick?: (c: { kind: "x" | "note"; url: string }) => void }) {
  const reps = Math.ceil(MIN_PER_ROW / items.length);
  const half = Array.from({ length: reps }, (_, r) => items.map((item) => ({ item, hidden: r > 0, rep: r }))).flat();
  const track = [...half, ...half.map((c) => ({ ...c, hidden: true, rep: c.rep + reps }))];
  return (
    <div className="vw-row">
      <ul
        className={reverse ? "vw-track vw-rev" : "vw-track"}
        style={{ ["--wall-dur" as string]: `${half.length * SEC_PER_CARD * (reverse ? 1.15 : 1)}s` }}
      >
        {track.map((c) => (
          <WallCard key={`${c.rep}-${c.item.key}`} item={c.item} hidden={c.hidden} onCardClick={onCardClick} />
        ))}
      </ul>
    </div>
  );
}

export function VoiceWall({
  posts = VOICE_POSTS,
  notes = VOICE_NOTES,
  onCardClick,
  className,
}: {
  posts?: VoicePost[];
  notes?: VoiceNote[];
  onCardClick?: (c: { kind: "x" | "note"; url: string }) => void;
  className?: string;
}) {
  if (posts.length + notes.length === 0) return null;
  return (
    <div className={className ? `vw-wall ${className}` : "vw-wall"}>
      {splitRows(posts, notes).map((items, i) => (
        <WallRow key={i} items={items} reverse={i === 1} onCardClick={onCardClick} />
      ))}
    </div>
  );
}
